// Adapted from T3 Code packages/contracts ipc SSH schemas; MIT, docs/licenses/T3-Code-MIT.txt.

/**
 * Reaching an AFAR Environment on another machine over the user's own SSH.
 *
 * A System SSH Target names where; everything here says what the Desktop Client learned by going
 * there. The forward is loopback on both ends, so every URL in this module is a `127.0.0.1`
 * address that exists only while the forward does — a client that stores one has stored a fact
 * about this session, not about the destination.
 */
import * as Schema from "effect/Schema";

import type { EnvironmentAccessExchangeResult } from "./environmentAccess.ts";
import type { ExecutionEnvironmentDescriptor } from "./environment.ts";

/**
 * One SSH destination, after `ssh -G` has been asked what the alias means.
 *
 * `alias` is what the user typed and what `ssh` is handed; the rest is what the user's own
 * configuration resolved it to, kept so a connection's identity survives a configuration edit
 * that changes what the alias points at.
 */
export const SshEnvironmentTarget = Schema.Struct({
  alias: Schema.String,
  hostname: Schema.String,
  username: Schema.NullOr(Schema.String),
  port: Schema.NullOr(Schema.Int),
});
export type SshEnvironmentTarget = typeof SshEnvironmentTarget.Type;

/** Where an offered destination was read from. Shown, so a user can tell a guess from a record. */
export const SshHostSource = Schema.Literals(["ssh-config", "known-hosts"]);
export type SshHostSource = typeof SshHostSource.Type;

export const DiscoveredSshHost = Schema.Struct({
  ...SshEnvironmentTarget.fields,
  source: SshHostSource,
});
export type DiscoveredSshHost = typeof DiscoveredSshHost.Type;

/**
 * Whether the destination's Environment Service was already running.
 *
 * `external` is one the machine's owner runs for themselves, which AFAR reuses and must never
 * stop on disconnect; `managed` is one this client started and therefore owns.
 */
export const SshRemoteServiceKind = Schema.Literals(["external", "managed"]);
export type SshRemoteServiceKind = typeof SshRemoteServiceKind.Type;

/**
 * What an open forward gives a client.
 *
 * `bootstrapCredential` is the one-time Environment Access grant the destination's own
 * `afar-research` minted, present only when the caller asked for one: reaching a machine over SSH
 * is never authorization by itself, so a client still redeems a credential the destination signed
 * for.
 */
export const SshEnvironmentBootstrap = Schema.Struct({
  target: SshEnvironmentTarget,
  httpBaseUrl: Schema.String,
  wsBaseUrl: Schema.String,
  bootstrapCredential: Schema.NullOr(Schema.String),
  remotePort: Schema.Int,
  remoteServiceKind: Schema.optionalKey(SshRemoteServiceKind),
});
export type SshEnvironmentBootstrap = typeof SshEnvironmentBootstrap.Type;

export const SshPasswordPromptRequest = Schema.Struct({
  requestId: Schema.String,
  destination: Schema.String,
  username: Schema.NullOr(Schema.String),
  prompt: Schema.String,
  expiresAt: Schema.String,
});
export type SshPasswordPromptRequest = typeof SshPasswordPromptRequest.Type;

/**
 * A cancelled prompt is a result, not a failure.
 *
 * A person who closes the password dialog has answered the question. Reporting that as an error
 * would send a surface down the retry ladder for a decision the user already made.
 */
export const SSH_PASSWORD_PROMPT_CANCELLED = "ssh-password-prompt-cancelled" as const;

export const SshPasswordPromptCancelled = Schema.Struct({
  type: Schema.Literal(SSH_PASSWORD_PROMPT_CANCELLED),
  message: Schema.String,
});
export type SshPasswordPromptCancelled = typeof SshPasswordPromptCancelled.Type;

/**
 * First contact with a machine whose key is not on record.
 *
 * The fingerprint is shown in full and never redacted: comparing it against what the destination
 * reports is the whole question. It is public information — the point is that it is the *right*
 * public information.
 */
export const SshHostKeyPromptRequest = Schema.Struct({
  requestId: Schema.String,
  destination: Schema.String,
  /** `ED25519`, `ECDSA`, `RSA`. */
  keyType: Schema.String,
  /** `SHA256:…`, exactly as `ssh` prints it on first contact. */
  fingerprint: Schema.String,
  expiresAt: Schema.String,
});
export type SshHostKeyPromptRequest = typeof SshHostKeyPromptRequest.Type;

export const SshHostKeyPromptResolutionInput = Schema.Struct({
  requestId: Schema.String,
  accepted: Schema.Boolean,
});
export type SshHostKeyPromptResolutionInput = typeof SshHostKeyPromptResolutionInput.Type;

export const SshEnvironmentEnsureOptions = Schema.Struct({
  issueBootstrapCredential: Schema.optionalKey(Schema.Boolean),
  /**
   * Install the Desktop's own Environment Service build on the destination and start the service
   * from it. Only a development Desktop carries a build to install; a release reaches what the
   * destination already has.
   */
  installService: Schema.optionalKey(Schema.Boolean),
});
export type SshEnvironmentEnsureOptions = typeof SshEnvironmentEnsureOptions.Type;

export const SshEnvironmentEnsureInput = Schema.Struct({
  target: SshEnvironmentTarget,
  options: Schema.optionalKey(SshEnvironmentEnsureOptions),
});
export type SshEnvironmentEnsureInput = typeof SshEnvironmentEnsureInput.Type;

export const SshEnvironmentEnsureResult = Schema.Union([
  SshEnvironmentBootstrap,
  SshPasswordPromptCancelled,
]);
export type SshEnvironmentEnsureResult = typeof SshEnvironmentEnsureResult.Type;

/**
 * The descriptor is protected, so it is read with the session the grant exchange just returned.
 * Every released Environment Service answers an unauthenticated read with 401.
 */
export const SshDescriptorInput = Schema.Struct({
  httpBaseUrl: Schema.String,
  session: Schema.String,
});
export type SshDescriptorInput = typeof SshDescriptorInput.Type;

export const SshRedeemInput = Schema.Struct({
  httpBaseUrl: Schema.String,
  bootstrapCredential: Schema.String,
});
export type SshRedeemInput = typeof SshRedeemInput.Type;

export const SshPasswordPromptResolutionInput = Schema.Struct({
  requestId: Schema.String,
  password: Schema.NullOr(Schema.String),
});
export type SshPasswordPromptResolutionInput = typeof SshPasswordPromptResolutionInput.Type;

/**
 * Desktop-only. A forward is a process on the machine the client runs on, so a browser client has
 * no bridge and never offers an SSH connection at all.
 */
export interface SshEnvironmentDesktopBridge {
  discoverHosts: () => Promise<readonly DiscoveredSshHost[]>;
  resolveHost: (alias: string) => Promise<SshEnvironmentTarget>;
  ensureEnvironment: (
    target: SshEnvironmentTarget,
    options?: SshEnvironmentEnsureOptions,
  ) => Promise<SshEnvironmentEnsureResult>;
  disconnectEnvironment: (target: SshEnvironmentTarget) => Promise<void>;
  /**
   * The renderer cannot reach a loopback forward the main process owns, so these three requests
   * are proxied. Each one refuses a URL that is not loopback.
   */
  fetchDescriptor: (
    httpBaseUrl: string,
    session: string,
  ) => Promise<ExecutionEnvironmentDescriptor>;
  redeemBootstrapCredential: (
    httpBaseUrl: string,
    bootstrapCredential: string,
  ) => Promise<EnvironmentAccessExchangeResult>;
  onPasswordPrompt: (listener: (request: SshPasswordPromptRequest) => void) => () => void;
  resolvePasswordPrompt: (requestId: string, password: string | null) => Promise<void>;
  /**
   * The question `ssh` asks on first contact. Separate from the password prompt because it is a
   * decision about identity rather than a secret, and the person answers it differently.
   */
  onHostKeyPrompt: (listener: (request: SshHostKeyPromptRequest) => void) => () => void;
  resolveHostKeyPrompt: (requestId: string, accepted: boolean) => Promise<void>;
}
